import { useRef, useEffect, useState } from 'react';
import { FiHeart, FiRepeat } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { trackEvent } from '../utils/tracking';
import { useSlabData } from '../hooks/useSlabData';
import { dektonSamples, caesarstoneSamples, marbleGraniteSamples, porcelainSamples } from './Sidebar';
import './MobileBottomSheet.css';

// ── EF slab lookup (mirrors MaterialDetails logic) ────────────────────────────
const _norm = (str) =>
  String(str).toLowerCase().replace(/[\s\-_]/g, '').replace(/[^a-z0-9]/g, '');

const ALL_EF_SAMPLES = [
  ...dektonSamples,
  ...caesarstoneSamples,
  ...marbleGraniteSamples,
  ...porcelainSamples,
];

const EF_SAMPLE_MAP = {};
ALL_EF_SAMPLES.forEach(s => {
  const k1 = _norm(s.label);
  if (k1 && !EF_SAMPLE_MAP[k1]) EF_SAMPLE_MAP[k1] = s;
  if (s.title) {
    const k2 = _norm(s.title.split(' - ').pop());
    if (k2 && !EF_SAMPLE_MAP[k2]) EF_SAMPLE_MAP[k2] = s;
  }
});

// ── n8n compare webhook — same endpoint as CompareModal ──────────────────────
const fireCompareWebhook = (itemA, itemB) => {
  fetch('https://elegantfittings.app.n8n.cloud/webhook/color', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      timestamp: new Date().toISOString(),
      colors: [
        { id: itemA.id, productName: itemA.label || itemA.productName, brandName: itemA.category || itemA.brandName || 'DEKTON', productImage: itemA.image || itemA.fullImage || itemA.productImage },
        { id: itemB.id, productName: itemB.label || itemB.productName, brandName: itemB.category || itemB.brandName || 'DEKTON', productImage: itemB.image || itemB.fullImage || itemB.productImage },
      ],
    }),
  }).catch(err => console.error('Compare webhook error:', err));
};

const findEFSample = (similarColor) => {
  const nName = _norm(similarColor.name || '');
  if (!nName) return null;
  if (EF_SAMPLE_MAP[nName]) return EF_SAMPLE_MAP[nName];
  return ALL_EF_SAMPLES.find(s => {
    const sNorm = _norm(s.label);
    return sNorm.length >= 4 && (nName.includes(sNorm) || sNorm.includes(nName));
  }) || null;
};

// ── Fallback brand data ───────────────────────────────────────────────────────
const BRAND_DATA = {
  'Dekton':        { tagline: 'Ultracompact Sintered Stone',  finishes: ['Polished', 'Velvet', 'Matte'], thicknesses: ['4mm', '8mm', '12mm', '20mm'], applications: ['Countertops', 'Vanities', 'Facades', 'Flooring'] },
  'Caesarstone':   { tagline: 'Premium Quartz Surface',       finishes: ['Polished', 'Honed', 'Natural'], thicknesses: ['13mm', '20mm', '30mm'],       applications: ['Countertops', 'Vanities', 'Wall Cladding'] },
  'Granite/Marble':{ tagline: 'Natural Stone',                finishes: ['Polished', 'Leathered', 'Honed'], thicknesses: ['20mm', '30mm'],             applications: ['Countertops', 'Vanities', 'Flooring'] },
  'Porcelain':     { tagline: 'Large Format Porcelain',       finishes: ['Polished', 'Silk', 'Matte'],   thicknesses: ['6mm', '12mm', '20mm'],         applications: ['Countertops', 'Vanities', 'Wall Cladding', 'Flooring'] },
};

const MAINT_LABELS = { none: 'None', low: 'Low', moderate: 'Moderate' };
const MAINT_COLORS = { none: '#4caf50', low: '#8bc34a', moderate: '#ff9800' };
const PRICE_LABELS = { entry: 'Entry', mid: 'Mid-range', premium: 'Premium', luxury: 'Luxury' };

const DurabilityDots = ({ score, max = 5 }) => (
  <span className="durability-dots">
    {Array.from({ length: max }, (_, i) => (
      <span key={i} className={`durability-dot${i < score ? ' filled' : ''}`} />
    ))}
  </span>
);

const CABINET_COLOURS = [
  { id: '', swatch: '/mainpng2-cab.png', name: 'Original' },
  { id: 'W', swatch: '/mainpng2-white-cab.png', name: 'White' },
  { id: 'B', swatch: '/mainpng2-B-cab.png', name: 'Blue' },
  { id: 'BL', swatch: '/mainpng2-BL-cab.png', name: 'Black' },
  { id: 'CR', swatch: '/mainpng2-CR-cab.png', name: 'Cream' },
  { id: 'G', swatch: '/mainpng2-G-cab.png', name: 'Green' },
  { id: '2T', swatch: null, name: '2 Tone' },
];

const MobileBottomSheet = ({
  activeSample,
  activeView,
  kitchenView,
  cabinetColor,
  setCabinetColor,
  upperCab = 'green',
  setUpperCab,
  lowerCab = 'black',
  setLowerCab,
  onToggleLike,
  isVisualLiked,
  onToggleCompare,
  isItemInCompare,
  compareCount = 0,
  onToggleSampleSelection,
  selectedSamples = [],
  onSampleClick,
  activeOverlay,
  sceneImage,
  inline = false,
}) => {
  const twoTonePickersRef = useRef(null);
  const sheetRef = useRef(null);
  const similarRef = useRef(null);
  const inCardSliderRef = useRef(null);
  const inCardDragging = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const [inCardCompareSlab, setInCardCompareSlab] = useState(null);
  const [inCardSliderPct, setInCardSliderPct] = useState(50);
  const { findSlab } = useSlabData();

  // Reset collapse when a new sample is selected
  useEffect(() => {
    setExpanded(false);
    setInCardCompareSlab(null);
    setInCardSliderPct(50);
  }, [activeSample?.id]);

  // In-card slider drag handlers
  useEffect(() => {
    const onMove = (e) => {
      if (!inCardDragging.current || !inCardSliderRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const { left, width } = inCardSliderRef.current.getBoundingClientRect();
      const pct = Math.min(100, Math.max(0, ((clientX - left) / width) * 100));
      setInCardSliderPct(pct);
    };
    const onUp = () => { inCardDragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  // Apply expanded class to the scroll container and wrap
  useEffect(() => {
    const scrollEl = sheetRef.current?.closest('.mobile-inline-detail');
    const wrapEl = sheetRef.current?.closest('.mobile-detail-wrap');
    if (!scrollEl) return;
    scrollEl.classList.toggle('is-expanded', expanded);
    if (wrapEl) wrapEl.classList.toggle('is-expanded', expanded);
  }, [expanded]);

  // Subtle scroll bounce — fires once per material to hint the panel scrolls
  useEffect(() => {
    if (!activeSample || !sheetRef.current) return;
    const scrollEl = sheetRef.current.closest('.mobile-inline-detail');
    if (!scrollEl) return;
    const t1 = setTimeout(() => {
      scrollEl.scrollTo({ top: 44, behavior: 'smooth' });
      const t2 = setTimeout(() => {
        scrollEl.scrollTo({ top: 0, behavior: 'smooth' });
      }, 480);
      return () => clearTimeout(t2);
    }, 700);
    return () => clearTimeout(t1);
  }, [activeSample?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!inline && !activeSample) return null;

  const isLiked   = activeSample && isVisualLiked  && isVisualLiked(activeSample.id);
  const inCompare = activeSample && isItemInCompare && isItemInCompare(activeSample.id);
  const showCabinet = activeSample && activeView === 'Kitchens' && kitchenView === 1;

  // Placeholder
  if (inline && !activeSample) {
    return (
      <div className="mbs-sheet mbs-sheet--inline mbs-sheet--empty">
        <p className="mbs-empty-hint">Tap a material to see details</p>
      </div>
    );
  }

  // Resolve rich JSON data
  const slabData = findSlab(activeSample.fullImage);
  const brand = BRAND_DATA[activeSample.category] || BRAND_DATA['Dekton'];

  const displayName = slabData?.name || activeSample.label;
  const displayDesc = slabData?.description || null;
  const finishes     = slabData?.finishes     || brand.finishes;
  const thicknesses  = slabData?.thicknesses  || brand.thicknesses;
  const applications = slabData?.applications || brand.applications;
  const collection   = slabData?.collection   || null;
  const stoneType    = slabData?.stoneType    || slabData?.material || null;

  // Similar colors — EF-carried only
  const carriedSimilar = slabData?.similarColors
    ?.map(s => ({ ...s, efSample: findEFSample(s) }))
    .filter(s => s.efSample)
    .slice(0, 3) || [];

  const scrollToSimilar = () => {
    if (expanded) {
      // Already expanded — scroll the sheet (it is the overflow container)
      if (similarRef.current && sheetRef.current) {
        const sheetTop = sheetRef.current.getBoundingClientRect().top;
        const simTop  = similarRef.current.getBoundingClientRect().top;
        sheetRef.current.scrollBy({ top: simTop - sheetTop - 12, behavior: 'smooth' });
      }
    } else {
      // Expand first, then scroll after the panel finishes opening
      setExpanded(true);
      setTimeout(() => {
        if (similarRef.current && sheetRef.current) {
          const sheetTop = sheetRef.current.getBoundingClientRect().top;
          const simTop  = similarRef.current.getBoundingClientRect().top;
          sheetRef.current.scrollBy({ top: simTop - sheetTop - 12, behavior: 'smooth' });
        }
      }, 420);
    }
  };

  const sheetClass = `mbs-sheet${inline ? ' mbs-sheet--inline' : ''}`;

  // ── EF-style expanded product page ──────────────────────────────────────────
  if (expanded) {
    return (
      <div className={`${sheetClass} mbs-sheet--expanded-view`} ref={sheetRef}>

        {/* ── Sticky breadcrumb bar — always visible ── */}
        <div className="mbs-ef-breadcrumb">
          <div className="mbs-handle" />
          <div className="mbs-ef-breadcrumb-row">
            <span className="mbs-ef-breadcrumb-text">
              {activeSample.category.toUpperCase()}
              {collection || stoneType ? ` · ${[collection, stoneType].filter(Boolean).join(' · ')}` : ''}
            </span>
            <button
              className="mbs-ef-close"
              onClick={() => setExpanded(false)}
              aria-label="Collapse"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M3 3L13 13M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Slab hero — or in-card compare slider */}
        {inCardCompareSlab ? (
          <div
            className="mbs-incard-slider"
            ref={inCardSliderRef}
            onMouseDown={(e) => { inCardDragging.current = true; const { left, width } = inCardSliderRef.current.getBoundingClientRect(); setInCardSliderPct(Math.min(100, Math.max(0, ((e.clientX - left) / width) * 100))); }}
            onTouchStart={(e) => { inCardDragging.current = true; const { left, width } = inCardSliderRef.current.getBoundingClientRect(); setInCardSliderPct(Math.min(100, Math.max(0, ((e.touches[0].clientX - left) / width) * 100))); }}
          >
            {/* Right — similar color */}
            <div className="mbs-incard-slider-right">
              <img src={inCardCompareSlab.image} alt={inCardCompareSlab.label} />
              <span className="mbs-incard-label mbs-incard-label--right">{inCardCompareSlab.label}</span>
            </div>
            {/* Left — active slab, clipped */}
            <div className="mbs-incard-slider-left" style={{ width: `${inCardSliderPct}%` }}>
              {activeOverlay && (
                <img src={activeOverlay} alt={displayName} style={{ width: inCardSliderRef.current?.offsetWidth ? `${inCardSliderRef.current.offsetWidth}px` : '100vw' }} />
              )}
              <span className="mbs-incard-label">{displayName}</span>
            </div>
            {/* Divider */}
            <div className="mbs-incard-divider" style={{ left: `${inCardSliderPct}%` }}>
              <div className="mbs-incard-handle">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M8 6L3 12L8 18M16 6L21 12L16 18"/>
                </svg>
              </div>
            </div>
            {/* Close compare */}
            <button className="mbs-incard-close" onClick={() => setInCardCompareSlab(null)} aria-label="Close compare">×</button>
          </div>
        ) : (
          <div className="mbs-ef-hero">
            {activeOverlay && (
              <img src={activeOverlay} alt={displayName} className="mbs-ef-hero-img" />
            )}
          </div>
        )}

        {/* Name + specs + WhatsApp order + heart */}
        <div className="mbs-ef-namerow">
          <div className="mbs-ef-nameblock">
            <span className="mbs-ef-name">{displayName}</span>
            <span className="mbs-ef-subtitle">
              {finishes[0]}{thicknesses.length ? ` · ${thicknesses.join(' / ')}` : ''}
            </span>
          </div>
          <div className="mbs-ef-namerow-actions">
            {(() => {
              const productId = activeSample?.id
                || (activeOverlay ? activeOverlay.split('/').pop().replace(/\.[^/.]+$/, '') : '');
              const shareUrl = productId
                ? `${window.location.origin}/?product=${encodeURIComponent(productId)}`
                : window.location.origin;
              const waText = `Hello Elegant Fittings! I'm interested in purchasing:\n\nProduct: ${displayName}\nPage: ${shareUrl}\n\nPlease advise on availability and pricing.`;
              const handleOrder = () => {
                fetch('https://elegantfittings.app.n8n.cloud/webhook/ef-whatsapp-order', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    product: displayName,
                    productId,
                    shareUrl,
                    brand: activeSample?.category || '',
                    timestamp: new Date().toISOString(),
                    source: 'visualizer-mobile-expanded-order',
                  }),
                }).catch(() => {});
              };
              return (
                <a
                  className="mbs-whatsapp-order-btn"
                  href={`https://wa.me/254745969500?text=${encodeURIComponent(waText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleOrder}
                >
                  <FaWhatsapp size={14} />
                  <span>Order</span>
                </a>
              );
            })()}
            <button
              className={`mbs-ef-heart ${isLiked ? 'is-liked' : ''}`}
              onClick={() => { onToggleLike && onToggleLike(activeSample); }}
              aria-label="Save"
            >
              <FiHeart size={20} />
            </button>
          </div>
        </div>

        {/* Two CTA pills */}
        {(() => {
          const inCart = selectedSamples.some(s => s.id === activeSample.id);
          return (
            <div className="mbs-ef-pills">
              <button
                className={`mbs-ef-pill ${inCart ? 'mbs-ef-pill--added' : 'mbs-ef-pill--primary'}`}
                onClick={() => onToggleSampleSelection && onToggleSampleSelection(activeSample)}
              >
                {inCart ? '✓ Added to Cart' : '+ Add to Cart'}
              </button>
              {carriedSimilar.length > 0 && (
                <button
                  className="mbs-ef-pill mbs-ef-pill--secondary"
                  onClick={() => {
                    similarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }}
                >
                  {carriedSimilar.length} similar color{carriedSimilar.length !== 1 ? 's' : ''} ↗
                </button>
              )}
            </div>
          );
        })()}

        {/* Lifestyle — scene background + slab overlay stacked (no blend mode) */}
        {sceneImage && (
          <div className="mbs-ef-lifestyle">
            <div className="mbs-ef-lifestyle-composite">
              {activeOverlay && (
                <img src={activeOverlay} alt={displayName} className="mbs-ef-lifestyle-scene" />
              )}
              <img src={sceneImage} alt={activeView} className="mbs-ef-lifestyle-overlay" />
            </div>
            <span className="mbs-ef-lifestyle-label">
              {activeView === 'Kitchens' ? 'KITCHEN COUNTERTOPS'
                : activeView === 'Vanities' ? 'BATHROOM VANITY'
                : 'WALL CLADDING'}
            </span>
          </div>
        )}

        {/* ── Material details ── */}
        <div className="mbs-ef-details">
          {displayDesc && (
            <p className="mbs-ef-desc">{displayDesc}</p>
          )}

          <div className="mbs-ef-specs">
            <div className="mbs-spec-row">
              <span className="mbs-spec-label">Finishes</span>
              <div className="mbs-tags">
                {finishes.slice(0, 4).map((f, i) => <span key={i} className="mbs-tag">{f}</span>)}
              </div>
            </div>
            <div className="mbs-spec-row">
              <span className="mbs-spec-label">Thicknesses</span>
              <span className="mbs-spec-value">{thicknesses.join(' · ')}</span>
            </div>
            <div className="mbs-spec-row">
              <span className="mbs-spec-label">Applications</span>
              <div className="mbs-tags">
                {applications.slice(0, 4).map((a, i) => <span key={i} className="mbs-tag mbs-tag--app">{a}</span>)}
              </div>
            </div>
          </div>

          {slabData && (
            <div className="slab-properties mbs-properties" style={{ margin: '8px 0 0' }}>
              {slabData.naturalStone !== undefined && (
                <div className="slab-prop">
                  <span className="slab-prop-value">{slabData.naturalStone ? '✓' : '✗'}</span>
                  <span className="slab-prop-label">Natural</span>
                </div>
              )}
              {slabData.outdoorRated !== undefined && (
                <div className="slab-prop">
                  <span className="slab-prop-value">{slabData.outdoorRated ? '✓' : '✗'}</span>
                  <span className="slab-prop-label">Outdoor</span>
                </div>
              )}
              {slabData.durabilityScore && (
                <div className="slab-prop">
                  <DurabilityDots score={slabData.durabilityScore} />
                  <span className="slab-prop-label">Durability</span>
                </div>
              )}
              {slabData.maintenanceLevel && (
                <div className="slab-prop">
                  <span className="slab-prop-value" style={{ color: MAINT_COLORS[slabData.maintenanceLevel] || '#555', fontSize: '11px' }}>
                    {MAINT_LABELS[slabData.maintenanceLevel] || slabData.maintenanceLevel}
                  </span>
                  <span className="slab-prop-label">Care</span>
                </div>
              )}
              {slabData.priceTier && (
                <div className="slab-prop">
                  <span className="slab-prop-value" style={{ fontSize: '11px' }}>
                    {PRICE_LABELS[slabData.priceTier] || slabData.priceTier}
                  </span>
                  <span className="slab-prop-label">Tier</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Similar Colors */}
        {carriedSimilar.length > 0 && (
          <div className="mbs-ef-similar" ref={similarRef}>
            <span className="mbs-ef-section-label">SIMILAR COLORS</span>
            <div className="mbs-ef-similar-grid">
              {carriedSimilar.map((s, i) => (
                <div key={i} className="mbs-ef-similar-card">
                  <div className="mbs-ef-similar-img-wrap">
                    <img
                      src={s.efSample.image}
                      alt={s.name}
                      className="mbs-ef-similar-img"
                      onClick={() => { onSampleClick && onSampleClick(s.efSample); setExpanded(false); }}
                    />
                    <button
                      className="mbs-similar-card-compare"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInCardCompareSlab(s.efSample);
                        setInCardSliderPct(50);
                        sheetRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                        fireCompareWebhook(activeSample, s.efSample);
                      }}
                    >
                      ⇄ Compare
                    </button>
                  </div>
                  <span className="mbs-ef-similar-name">{s.name}</span>
                  <span className="mbs-ef-similar-brand">{s.brand || s.collection || s.efSample.category}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cabinet colours — full version with 2-tone pickers */}
        {showCabinet && (
          <div className="mbs-cabinet-section" style={{ padding: '16px 16px' }}>
            <span className="mbs-section-label">CABINET COLOUR</span>
            <div className="mbs-cabinet-row">
              {CABINET_COLOURS.map((color) => (
                <div
                  key={color.id}
                  className={`mbs-cab-pill ${cabinetColor === color.id ? 'active' : ''}`}
                  onClick={() => setCabinetColor(color.id)}
                  title={color.name}
                >
                  {color.swatch ? <img src={color.swatch} alt={color.name} /> : <div className="mbs-cab-2t-swatch" />}
                </div>
              ))}
            </div>
            {cabinetColor === '2T' && (
              <div className="mbs-cabinet-2t-pickers">
                {[
                  { label: 'Upper', options: [
                    { val: '', color: '#C8B89A', name: 'Orig' },
                    { val: 'W',  color: '#F0EDE8', name: 'White' },
                    { val: 'B',  color: '#003764', name: 'Blue' },
                    { val: 'CR', color: '#E8D5B0', name: 'Cream' },
                    { val: 'G',  color: '#2D5A3D', name: 'Green' },
                    { val: 'BL', color: '#1A1A1A', name: 'Black' },
                  ], active: upperCab, setter: setUpperCab },
                  { label: 'Lower', options: [
                    { val: '', color: '#C8B89A', name: 'Orig' },
                    { val: 'W',  color: '#F0EDE8', name: 'White' },
                    { val: 'B',  color: '#003764', name: 'Blue' },
                    { val: 'BL', color: '#1A1A1A', name: 'Black' },
                    { val: 'CR', color: '#E8D5B0', name: 'Cream' },
                    { val: 'G',  color: '#2D5A3D', name: 'Green' },
                  ], active: lowerCab, setter: setLowerCab },
                ].map(({ label, options, active, setter }) => (
                  <div className="cabinet-2t-row" key={label}>
                    <span className="cabinet-2t-label">{label}</span>
                    {options.map(({ val, color, name }) => (
                      <div
                        key={val}
                        className={`cab-color-dot ${active === val ? 'active' : ''}`}
                        style={{ '--dot-color': color }}
                        title={name}
                        onClick={() => setter && setter(val)}
                      >
                        <span className="cab-color-dot-circle" />
                        {active === val && <span className="cab-color-dot-name">{name}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ height: 24 }} />
      </div>
    );
  }

  return (
    <>
      {!inline && <div className="mbs-backdrop" />}
      <div className={sheetClass} ref={sheetRef}>
        {!inline && <div className="mbs-handle-row"><div className="mbs-handle" /></div>}

        {/* ── Cabinet colours — Kitchen view 1, shown first for immediate access ── */}
        {showCabinet && (
          <div className="mbs-cabinet-section mbs-cabinet-section--top">
            <div className="mbs-cabinet-section-header">
              <span className="mbs-section-label">CABINET COLOUR</span>
              <button
                className={`mbs-expand-btn${expanded ? ' is-expanded' : ''}`}
                onClick={() => setExpanded(e => !e)}
                aria-label={expanded ? 'Collapse details' : 'Expand details'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 10L8 5L13 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="mbs-cabinet-row">
              {CABINET_COLOURS.map((color) => (
                <div
                  key={color.id}
                  className={`mbs-cab-pill ${cabinetColor === color.id ? 'active' : ''}`}
                  onClick={() => {
                    setCabinetColor(color.id);
                    trackEvent('cabinet_color_selected', { color: color.name, source: 'inline_panel' });
                    if (color.id === '2T') {
                      setTimeout(() => {
                        twoTonePickersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }, 50);
                    }
                  }}
                  title={color.name}
                >
                  {color.swatch
                    ? <img src={color.swatch} alt={color.name} />
                    : <div className="mbs-cab-2t-swatch" />
                  }
                </div>
              ))}
            </div>
            {(
              <div className="mbs-cabinet-2t-pickers" ref={twoTonePickersRef}>
                {[
                  { label: 'Upper', options: [
                    { val: '', color: '#C8B89A', name: 'Orig' },
                    { val: 'W',  color: '#F0EDE8', name: 'White' },
                    { val: 'B',  color: '#003764', name: 'Blue' },
                    { val: 'CR', color: '#E8D5B0', name: 'Cream' },
                    { val: 'G',  color: '#2D5A3D', name: 'Green' },
                    { val: 'BL', color: '#1A1A1A', name: 'Black' },
                  ], active: upperCab, setter: setUpperCab },
                  { label: 'Lower', options: [
                    { val: '', color: '#C8B89A', name: 'Orig' },
                    { val: 'W',  color: '#F0EDE8', name: 'White' },
                    { val: 'B',  color: '#003764', name: 'Blue' },
                    { val: 'BL', color: '#1A1A1A', name: 'Black' },
                    { val: 'CR', color: '#E8D5B0', name: 'Cream' },
                    { val: 'G',  color: '#2D5A3D', name: 'Green' },
                  ], active: lowerCab, setter: setLowerCab },
                ].map(({ label, options, active, setter }) => (
                  <div className="cabinet-2t-row" key={label}>
                    <span className="cabinet-2t-label">{label}</span>
                    {options.map(({ val, color, name }) => (
                      <div
                        key={val}
                        className={`cab-color-dot ${active === val ? 'active' : ''}`}
                        style={{ '--dot-color': color }}
                        title={name}
                        onClick={() => {
                          setter && setter(val);
                          setCabinetColor('2T');
                        }}
                      >
                        <span className="cab-color-dot-circle" />
                        {active === val && <span className="cab-color-dot-name">{name}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <div className="mbs-divider" />
          </div>
        )}

        {/* ── Header: name + brand tag + expand toggle ── */}
        <div className="mbs-header">
          <div className="mbs-name-col">
            <span className="mbs-name">{displayName}</span>
            <span className="mbs-tagline">
              {activeSample.category}
              {(collection || stoneType) ? ` · ${[collection, stoneType].filter(Boolean).join(' · ')}` : ` · ${brand.tagline}`}
            </span>
          </div>
          <div className="mbs-header-right">
            {carriedSimilar.length > 0 && (
              <button className="mbs-similar-cta" onClick={scrollToSimilar}>
                See {carriedSimilar.length} similar →
              </button>
            )}
            {!showCabinet && (
              <button
                className={`mbs-expand-btn${expanded ? ' is-expanded' : ''}`}
                onClick={() => setExpanded(e => !e)}
                aria-label={expanded ? 'Collapse details' : 'Expand details'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 10L8 5L13 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Description ── */}
        {displayDesc && (
          <div className="mbs-desc">{displayDesc}</div>
        )}

        {/* ── Specs grid ── */}
        <div className="mbs-specs">
          <div className="mbs-spec-row">
            <span className="mbs-spec-label">Finishes</span>
            <div className="mbs-tags">
              {finishes.slice(0, 4).map((f, i) => (
                <span key={i} className="mbs-tag">{f}</span>
              ))}
            </div>
          </div>

          <div className="mbs-spec-row">
            <span className="mbs-spec-label">Thicknesses</span>
            <span className="mbs-spec-value">{thicknesses.join(' · ')}</span>
          </div>

          <div className="mbs-spec-row">
            <span className="mbs-spec-label">Applications</span>
            <div className="mbs-tags">
              {applications.slice(0, 4).map((a, i) => (
                <span key={i} className="mbs-tag mbs-tag--app">{a}</span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Properties badges ── */}
        {slabData && (
          <div className="slab-properties mbs-properties">
            {slabData.naturalStone !== undefined && (
              <div className="slab-prop">
                <span className="slab-prop-value">{slabData.naturalStone ? '✓' : '✗'}</span>
                <span className="slab-prop-label">Natural</span>
              </div>
            )}
            {slabData.outdoorRated !== undefined && (
              <div className="slab-prop">
                <span className="slab-prop-value">{slabData.outdoorRated ? '✓' : '✗'}</span>
                <span className="slab-prop-label">Outdoor</span>
              </div>
            )}
            {slabData.durabilityScore && (
              <div className="slab-prop">
                <DurabilityDots score={slabData.durabilityScore} />
                <span className="slab-prop-label">Durability</span>
              </div>
            )}
            {slabData.maintenanceLevel && (
              <div className="slab-prop">
                <span className="slab-prop-value" style={{ color: MAINT_COLORS[slabData.maintenanceLevel] || '#555', fontSize: '11px' }}>
                  {MAINT_LABELS[slabData.maintenanceLevel] || slabData.maintenanceLevel}
                </span>
                <span className="slab-prop-label">Care</span>
              </div>
            )}
            {slabData.priceTier && (
              <div className="slab-prop">
                <span className="slab-prop-value" style={{ fontSize: '11px' }}>
                  {PRICE_LABELS[slabData.priceTier] || slabData.priceTier}
                </span>
                <span className="slab-prop-label">Tier</span>
              </div>
            )}
          </div>
        )}

        {/* ── Similar Colors — horizontal scroll portrait cards ── */}
        {carriedSimilar.length > 0 && (
          <div className="mbs-similar" ref={similarRef}>
            <span className="mbs-spec-label">Similar Colors</span>
            <div className="mbs-similar-scroll">
              {carriedSimilar.map((s, i) => (
                <div key={i} className="mbs-similar-card">
                  <div className="mbs-similar-card-img-wrap">
                    <img
                      src={s.efSample.image}
                      alt={s.name}
                      className="mbs-similar-card-img"
                      onClick={() => {
                        onSampleClick && onSampleClick(s.efSample);
                        const scrollEl = sheetRef.current?.closest('.mobile-inline-detail');
                        if (scrollEl) setTimeout(() => scrollEl.scrollTo({ top: 0, behavior: 'smooth' }), 50);
                      }}
                    />
                    {onToggleCompare && (
                      <button
                        className="mbs-similar-card-compare"
                        onClick={() => {
                          onToggleCompare(s.efSample);
                          onToggleCompare(activeSample);
                        }}
                      >
                        ⇄ Compare
                      </button>
                    )}
                  </div>
                  <p className="mbs-similar-card-name">{s.name}</p>
                  {(s.brand || s.collection) && (
                    <p className="mbs-similar-card-brand">{s.brand || s.collection}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}


        {/* ── Action row ── */}
        <div className="mbs-actions">
          <button
            className={`mbs-action-btn ${isLiked ? 'is-liked' : ''}`}
            onClick={() => {
              onToggleLike && onToggleLike(activeSample);
              trackEvent(isLiked ? 'visual_unliked' : 'visual_liked', { sample_id: activeSample.id, source: 'inline_panel' });
            }}
          >
            <FiHeart size={16} />
            <span>{isLiked ? 'Saved' : 'Save'}</span>
          </button>

          <button
            className={`mbs-action-btn ${inCompare ? 'is-compare' : ''}`}
            disabled={compareCount >= 2 && !inCompare}
            style={compareCount >= 2 && !inCompare ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            onClick={() => {
              if (compareCount >= 2 && !inCompare) return;
              onToggleCompare && onToggleCompare(activeSample);
              trackEvent('compare_item_toggled', { sample_id: activeSample.id, source: 'inline_panel' });
            }}
          >
            <FiRepeat size={16} />
            <span>Compare</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileBottomSheet;
